import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Phone, Building2, Calendar, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('leads'); // leads or contacts

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [leadsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/contacts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
    setLoading(false);
  };

  const currentData = activeTab === 'leads' ? leads : contacts;
  const filteredData = currentData.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2" data-testid="leads-title">Leads & Contacts</h1>
        <p className="text-muted-foreground">Manage inquiries from your website</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setActiveTab('leads')}
          data-testid="tab-leads"
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'leads'
              ? 'bg-blue-600 text-white'
              : 'bg-muted text-muted-foreground hover:text-foreground border border-[#E2E8F0]'
          }`}
        >
          Leads ({leads.length})
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          data-testid="tab-contacts"
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'contacts'
              ? 'bg-blue-600 text-white'
              : 'bg-muted text-muted-foreground hover:text-foreground border border-[#E2E8F0]'
          }`}
        >
          Contacts ({contacts.length})
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative max-w-md">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
        <input
          type="text"
          placeholder="Search by name, email, or message..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="search-leads"
          className="bg-background border border-[#CBD5E1] rounded-lg pl-12 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
        />
      </div>

      {/* List */}
      {filteredData.length === 0 ? (
        <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-12 rounded-2xl text-center">
          <p className="text-muted-foreground">
            {searchQuery ? 'No results found' : `No ${activeTab} yet`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredData.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`lead-${index}`}
              className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl hover:border-blue-200 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">{item.name}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Mail size={14} />
                      <a href={`mailto:${item.email}`} className="hover:text-blue-600 transition-colors">
                        {item.email}
                      </a>
                    </div>
                    {item.phone && (
                      <div className="flex items-center space-x-1">
                        <Phone size={14} />
                        <a href={`tel:${item.phone}`} className="hover:text-blue-600 transition-colors">
                          {item.phone}
                        </a>
                      </div>
                    )}
                    {item.company && (
                      <div className="flex items-center space-x-1">
                        <Building2 size={14} />
                        <span>{item.company}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs text-blue-700">
                    {item.status || 'New'}
                  </span>
                  <div className="flex items-center space-x-1 text-muted-foreground text-xs">
                    <Calendar size={12} />
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {item.subject && (
                <div className="mb-3">
                  <span className="text-sm font-medium text-muted-foreground">Subject: </span>
                  <span className="text-sm text-foreground">{item.subject}</span>
                </div>
              )}

              {item.service && (
                <div className="mb-3">
                  <span className="bg-violet-50 border border-violet-200 rounded-full px-3 py-1 text-xs text-violet-700">
                    {item.service}
                  </span>
                </div>
              )}

              <div className="bg-muted/50 border border-[#E2E8F0] p-4 rounded-lg">
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{item.message}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
